import { getAccessToken } from '../../../../Services/getAccessToken';
import { ENVIRONMENT_URL, RAJPURA_TABLES, getRajpuraTourId, sanitizeParentTourPayload } from '../../../../constants/dataverse';
import { putToStore, enqueueSync } from '../../../../Services/offlineDB';
import moment from 'moment';
import { PKG_PRODUCTS_SEED_DATA } from '../../packagingOperations/constants/pkgProductsSeed';

export const alcService = {
    getConfig: async function (accessToken: string) {
        if (!accessToken) throw new Error("Access token is missing");

        const { buildSharePointListItemsUrl, SP_SITE_URL,  } = await import('../../../../constants/sharepoint');

        try {
            console.log("Fetching QA Config Fields from SharePoint...");
            
            const listNames = ["Quality-Rajpura-ALC", "Quality-Rajpura", "QualityRajpura", "Quality Rajpura", "Quality Rajpura ALC", "Quality-Rajpura ALC", "Quality Rajpura-ALC"];
            let fieldsData: any = null;
            let activeListName = "";

            for (const name of listNames) {
                try {
                    const fieldsUrl = `${SP_SITE_URL}/_api/web/lists/getByTitle('${name}')/Fields?$select=InternalName,Title`;
                    const fieldsResponse = await fetch(fieldsUrl, {
                        headers: { "Accept": "application/json;odata=verbose", "Authorization": `Bearer ${accessToken}` }
                    });

                    if (fieldsResponse.ok) {
                        fieldsData = await fieldsResponse.json();
                        activeListName = name;
                        break;
                    }
                } catch (e) {
                    console.warn(`Failed to fetch fields for list ${name}`, e);
                }
            }

            // Bulletproof fallback: fetch ALL lists and find the one containing ALC or Rajpura
            if (!fieldsData) {
                try {
                    console.log("Probing all lists to find the correct one...");
                    const allListsUrl = `${SP_SITE_URL}/_api/web/lists?$select=Title,Id`;
                    const allListsResponse = await fetch(allListsUrl, {
                        headers: { "Accept": "application/json;odata=verbose", "Authorization": `Bearer ${accessToken}` }
                    });
                    if (allListsResponse.ok) {
                        const allListsData = await allListsResponse.json();
                        const lists = allListsData.d.results;
                        
                        console.log(`Found ${lists.length} total lists in the site.`);
                        
                        // Strip all spaces and hyphens to compare purely alphanumeric names
                        const cleanName = (name: string) => name ? name.toLowerCase().replace(/[^a-z0-9]/gi, '') : '';
                        
                        const targetCleanName = "qualityrajpuraalc";
                        const matchingList = lists.find((l: any) => cleanName(l.Title).includes(targetCleanName));
                        
                        if (matchingList) {
                            console.log(`Matched list! Display Name is: '${matchingList.Title}'`);
                            activeListName = matchingList.Title;
                            const fieldsUrl = `${SP_SITE_URL}/_api/web/lists/getByTitle('${activeListName}')/Fields?$select=InternalName,Title`;
                            const fieldsResponse = await fetch(fieldsUrl, {
                                headers: { "Accept": "application/json;odata=verbose", "Authorization": `Bearer ${accessToken}` }
                            });
                            if (fieldsResponse.ok) {
                                fieldsData = await fieldsResponse.json();
                            } else {
                                console.error(`Found list '${matchingList.Title}', but failed to fetch its fields.`);
                            }
                        } else {
                            console.warn("Could not find any list containing 'Quality Rajpura ALC' in its title. Available list titles:", lists.map((l:any) => l.Title).join(", "));
                        }
                    }
                } catch (e) {
                    console.error("Failed to list all lists", e);
                }
            }

            if (!fieldsData) {
                throw new Error("Could not find a valid SharePoint list for QA Config.");
            }

            let itemsUrl = "";
            const listFields = fieldsData.d?.results || [];

            const findField = (possibleNames: string[], displayName: string) => {
                let match = listFields.find((f: any) => possibleNames.includes(f.InternalName));
                if (match) return match;
                return listFields.find((f: any) => f.Title === displayName);
            };

            const configTypeField = findField(["ConfigType", "Config_x0020_Type"], "Config Type");
            const plantField = findField(["Plant"], "Plant");
            const regionField = findField(["Region"], "Region");
            const areaField = findField(["Area"], "Area");
            const assignedQAField = findField(["AssignedUser", "Assigned_x0020_User", "AssignedQA", "Assigned_x0020_QA", "QAExecutive"], "Assigned User");
            const escalationManagerField = findField(["EscalationManager", "Escalation_x0020_Manager"], "Escalation Manager");
            const qaShiftField = findField(["QAShiftExecutive", "QAShift_x0020_Executive", "QAShiftExec", "QAShift_x0020_Exec"], "QA Shift Executive");
            const lineNameField = findField(["LineName", "Line_x0020_Name"], "LineName");
            const shiftCodeField = findField(["ShiftCode", "Shift_x0020_Code"], "ShiftCode");
            const shiftNameField = findField(["ShiftName", "Shift_x0020_Name"], "ShiftName");

            const productCodeField = findField(["ProductCode", "Product_x0020_Code"], "Product Code");
            const productCategoryField = findField(["ProductCategory", "Product_x0020_Category"], "Product Category");
            const isActiveField = findField(["IsActive", "Is_x0020_Active"], "IsActive");
            const checklistTypeField = findField(["ChecklistType", "Checklist_x0020_Type"], "Checklist Type");

            const selectParts = ["Id", "Title"];
            const expandParts: string[] = [];

            if (configTypeField) selectParts.push(configTypeField.InternalName);
            if (plantField) selectParts.push(plantField.InternalName);
            if (regionField) selectParts.push(regionField.InternalName);
            if (areaField) selectParts.push(areaField.InternalName);
            if (lineNameField) selectParts.push(lineNameField.InternalName);
            if (shiftCodeField) selectParts.push(shiftCodeField.InternalName);
            if (shiftNameField) selectParts.push(shiftNameField.InternalName);
            if (productCodeField) selectParts.push(productCodeField.InternalName);
            if (productCategoryField) selectParts.push(productCategoryField.InternalName);
            if (isActiveField) selectParts.push(isActiveField.InternalName);
            if (checklistTypeField) selectParts.push(checklistTypeField.InternalName);

            if (assignedQAField) {
                const name = assignedQAField.InternalName;
                selectParts.push(`${name}/Title`, `${name}/EMail`, `${name}/Id`);
                expandParts.push(name);
            }
            if (escalationManagerField) {
                const name = escalationManagerField.InternalName;
                selectParts.push(`${name}/Title`, `${name}/EMail`, `${name}/Id`);
                expandParts.push(name);
            }
            if (qaShiftField) {
                const name = qaShiftField.InternalName;
                selectParts.push(`${name}/Title`, `${name}/EMail`, `${name}/Id`);
                expandParts.push(name);
            }

            let query = `$select=${selectParts.join(",")}`;
            if (expandParts.length > 0) {
                query += `&$expand=${expandParts.join(",")}`;
            }
            
            // Fetch for Rajpura with top=5000 to return all products in a single request
            query += `&$filter=${plantField ? plantField.InternalName : 'Plant'} eq 'Rajpura'&$top=5000`;

            itemsUrl = buildSharePointListItemsUrl(activeListName, query);
            
            const response = await fetch(itemsUrl, {
                headers: { "Accept": "application/json;odata=verbose", "Authorization": `Bearer ${accessToken}` }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch SharePoint config items: ${response.statusText}`);
            }

            const data = await response.json();
            const results = data.d.results || [];

            const finalResults = results.map((item: any) => ({
                Id: item.Id,
                Title: item.Title,
                ConfigType: configTypeField ? item[configTypeField.InternalName] : item.ConfigType,
                Region: regionField ? item[regionField.InternalName] : item.Region,
                Plant: plantField ? item[plantField.InternalName] : item.Plant,
                Area: areaField ? item[areaField.InternalName] : item.Area,
                AssignedUser: assignedQAField ? item[assignedQAField.InternalName] : item.AssignedUser,
                EscalationManager: escalationManagerField ? item[escalationManagerField.InternalName] : item.EscalationManager,
                QAShiftExecutive: qaShiftField ? item[qaShiftField.InternalName] : item.QAShiftExecutive,
                LineName: lineNameField ? item[lineNameField.InternalName] : item.LineName,
                ShiftCode: shiftCodeField ? item[shiftCodeField.InternalName] : item.ShiftCode,
                ShiftName: shiftNameField ? item[shiftNameField.InternalName] : item.ShiftName,
                ProductCode: productCodeField ? item[productCodeField.InternalName] : item.ProductCode,
                ProductCategory: productCategoryField ? item[productCategoryField.InternalName] : item.ProductCategory,
                IsActive: isActiveField ? item[isActiveField.InternalName] : (item.IsActive !== undefined ? item.IsActive : true),
                ChecklistType: checklistTypeField ? item[checklistTypeField.InternalName] : item.ChecklistType
            }));

            try {
                localStorage.setItem('alc_qa_config_cache', JSON.stringify(finalResults));
            } catch (e) {
                console.warn('Failed to cache QA config', e);
            }
            
            return finalResults;

        } catch (error) {
            console.warn("Failed to load QA config from SharePoint list (likely offline), using local cache:", error);
            try {
                const cached = localStorage.getItem('alc_qa_config_cache');
                if (cached) {
                    const parsed = JSON.parse(cached);
                    if (Array.isArray(parsed) && parsed.length > 0) {
                        return parsed;
                    }
                }
            } catch (e) {
                console.warn('Failed to parse cached QA config', e);
            }
            return [];
        }
    },

    // Helper: Normalize and extract Line Number from any string / format matching qa-request.js
    extractLineNumber: function (str?: string): number | null {
        if (!str) return null;
        const s = String(str).trim();
        // Match expressions like "Line 2", "Line No. 2", "Line No 2", "Line-2", "Line_2", "Line#2", "L2", "L-2", "Line 02", "Line No: 2"
        const m = s.match(/(?:line|ln|l)?\s*(?:no\.?|num\.?|#|-|_|:)?\s*0*(\d+)/i);
        if (m && m[1]) return parseInt(m[1], 10);
        const digitMatch = s.match(/\b\d+\b/);
        return digitMatch ? parseInt(digitMatch[0], 10) : null;
    },

    /**
     * Get active products dynamically from SharePoint config items matching qa-request.js populateProductSelection()
     */
    getProducts: function (configs: any[], selectedLine?: string): string[] {
        let rawList: any[] = [];
        if (Array.isArray(configs) && configs.length > 0) {
            rawList = [...configs];
        }

        // Also check cached local storage lists to ensure complete product master dataset
        const cacheKeys = ['alc_qa_config_cache', 'quality_rajpura_config', 'food_safety_config'];
        for (const key of cacheKeys) {
            try {
                const cached = localStorage.getItem(key);
                if (cached) {
                    const parsed = JSON.parse(cached);
                    if (Array.isArray(parsed)) {
                        rawList.push(...parsed);
                    }
                }
            } catch (e) {
                // ignore
            }
        }

        let products = rawList.filter(c => {
            if (!c) return false;
            const configType = String(c.ConfigType || c.Config_x0020_Type || c.ChecklistType || c.Checklist_x0020_Type || "");
            const isProduct = configType === "Product Master" || configType.toLowerCase().includes("product master") || configType.toLowerCase().includes("product catalogue");
            if (!isProduct) return false;
            if (c.IsActive === false || c.IsActive === "false" || c.IsActive === 0) return false;
            return true;
        });

        // Line-based filtering strictly by selected Line Number
        const selectedLineNum = this.extractLineNumber(selectedLine);
        if (selectedLine && selectedLine.trim() && selectedLine.toLowerCase() !== "select line" && selectedLineNum !== null) {
            products = products.filter(p => {
                const pLineRaw = String(
                    p.LineName ||
                    p.lineName ||
                    p.Line ||
                    p.line ||
                    p.AssociatedLine ||
                    p.associatedLine ||
                    p.Line_x0020_Name ||
                    p.cr3ea_linename ||
                    p.cr3ea_line ||
                    ""
                ).trim();

                // 1. Products with empty line or "All Lines", "Default", "General" match all lines
                if (!pLineRaw || /^(all(\s*lines)?|default|general)$/i.test(pLineRaw)) {
                    return true;
                }

                // 2. Strict Line Number matching (e.g. Line 2, Line No. 2, Line-2 -> matches selected line number 2)
                const pLineNum = this.extractLineNumber(pLineRaw);
                if (pLineNum !== null) {
                    return selectedLineNum === pLineNum;
                }

                return false;
            });
        }

        const formattedProducts: string[] = [];
        const seen = new Set<string>();

        products.forEach(item => {
            let productFormatted = "";
            if (typeof item === 'string') {
                productFormatted = item.trim();
            } else if (item.Title || item.title) {
                const title = (item.Title || item.title).trim();
                const productCode = item.ProductCode || item.Product_x0020_Code || item.productCode || item.SKU || "";
                const codeSuffix = (productCode && !title.includes(productCode)) ? ` (${productCode})` : "";
                productFormatted = `${title}${codeSuffix}`;
            }

            if (productFormatted && !seen.has(productFormatted.toLowerCase())) {
                seen.add(productFormatted.toLowerCase());
                formattedProducts.push(productFormatted);
            }
        });

        if (formattedProducts.length === 0 && PKG_PRODUCTS_SEED_DATA && Array.isArray(PKG_PRODUCTS_SEED_DATA)) {
            const seedFiltered = PKG_PRODUCTS_SEED_DATA.filter(p => {
                if (!selectedLine || !selectedLine.trim() || selectedLine.toLowerCase() === "select line") return true;
                const lineNum = this.extractLineNumber(selectedLine);
                const pLineNum = this.extractLineNumber(p.lineName);
                if (lineNum !== null && pLineNum !== null) {
                    return lineNum === pLineNum;
                }
                return true;
            });

            seedFiltered.forEach(item => {
                const title = (item.title || "").trim();
                const productCode = item.productCode || "";
                const codeSuffix = (productCode && !title.includes(productCode)) ? ` (${productCode})` : "";
                const productFormatted = `${title}${codeSuffix}`;
                if (productFormatted && !seen.has(productFormatted.toLowerCase())) {
                    seen.add(productFormatted.toLowerCase());
                    formattedProducts.push(productFormatted);
                }
            });
        }

        return formattedProducts.sort((a, b) => a.localeCompare(b));
    },

    getActiveSessions: async function (): Promise<any[]> {
        const tokenResult = await getAccessToken(undefined, undefined, true);
        const accessToken = tokenResult?.token;
        if (!accessToken) throw new Error("Access token is missing");

        const tableName = RAJPURA_TABLES.QUALITY_TOUR;
        const apiVersion = "9.2";
        const todayStart = moment().startOf('day').toISOString();
        const filter = `?$filter=createdon ge ${todayStart} or cr3ea_tourstartdate ge '${todayStart}'&$orderby=createdon desc&$top=50`;
        const url = `${ENVIRONMENT_URL}/api/data/v${apiVersion}/${tableName}${filter}`;

        try {
            const response = await fetch(url, {
                headers: {
                    "Accept": "application/json",
                    "Authorization": `Bearer ${accessToken}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                return data.value || [];
            }
        } catch (e) {
            console.warn("Failed to fetch active sessions:", e);
        }
        return [];
    },

    saveSession: async function (sessionData: any) {
        const tokenResult = await getAccessToken(undefined, undefined, true);
        const accessToken = tokenResult?.token;
        if (!accessToken) throw new Error("Access token is missing");

        const apiVersion = "9.2";
        const tourIdVal = getRajpuraTourId(sessionData);
        let method: 'POST' | 'PATCH' = (tourIdVal && !sessionData._isNew) ? 'PATCH' : 'POST';
        const sessionPayload = { ...sessionData };
        delete sessionPayload._isNew;

        if (sessionPayload.cr3ea_status && !sessionPayload.cr3ea_processstatus) {
            sessionPayload.cr3ea_processstatus = sessionPayload.cr3ea_status;
        }

        if (!navigator.onLine) {
            // OFFLINE MODE
            console.warn("Offline: saving session to local DB", sessionPayload);
            const id = tourIdVal;
            if (id) {
                await putToStore('alc_sessions', { id, data: sessionPayload, syncStatus: 'pending', updatedAt: Date.now() });
                await enqueueSync({
                    id: `${Date.now()}_session_${id}`,
                    type: 'session',
                    method: method,
                    payload: sessionPayload,
                    createdAt: Date.now()
                });
            }
            return sessionPayload;
        }

        const tableCandidates = [
            RAJPURA_TABLES.QUALITY_TOUR
        ];

        let response: Response | null = null;
        let lastErrText = "";

        for (const tableName of tableCandidates) {
            const currentPayload = sanitizeParentTourPayload(sessionPayload);
            let url = `${ENVIRONMENT_URL}/api/data/v${apiVersion}/${tableName}`;
            if (method === "PATCH" && tourIdVal) {
                url += `(${tourIdVal})`;
            }

            delete currentPayload.cr3ea_prod_rajpura_quality_tourid;
            delete currentPayload.cr3ea_rajpura_quality_tourid;
            delete currentPayload.cr3ea_prod_qualitytourid;
            delete currentPayload.cr3ea_qualitytourid;
            delete currentPayload.id;

            try {
                const res = await fetch(url, {
                    method: method,
                    headers: {
                        "Accept": "application/json",
                        "Content-Type": "application/json; charset=utf-8",
                        "OData-MaxVersion": "4.0",
                        "OData-Version": "4.0",
                        "Prefer": "return=representation",
                        "Authorization": `Bearer ${accessToken}`
                    },
                    body: JSON.stringify(currentPayload)
                });
                if (res.ok) {
                    response = res;
                    break;
                } else {
                    lastErrText = await res.text();
                    console.warn(`Dataverse session save to '${tableName}' failed (${res.status}):`, lastErrText);
                }
            } catch (e) {
                lastErrText = String(e);
            }
        }

        if (!response || !response.ok) {
            throw new Error(`Dataverse session save failed: ${lastErrText}`);
        }

        const resultData = method === "PATCH" ? sessionData : await response.json();
        const savedId = getRajpuraTourId(resultData) || tourIdVal;

        if (savedId) {
            resultData.cr3ea_prod_rajpura_quality_tourid = savedId;
            resultData.cr3ea_rajpura_quality_tourid = savedId;
            resultData.cr3ea_prod_qualitytourid = savedId;
            await putToStore('alc_sessions', { id: savedId, data: resultData, syncStatus: 'synced', updatedAt: Date.now() });
        }

        return resultData;
    },

    saveChecklistRow: async function (rowRecord: any) {
        const payload = { ...rowRecord };
        delete payload.id;

        const alcIdVal = payload.cr3ea_prod_rajpura_alcsid || payload.cr3ea_rajpura_alcsid;
        let method: 'POST' | 'PATCH' = (alcIdVal && !payload._isNew) ? 'PATCH' : 'POST';
        delete payload._isNew;

        const cleanTourId = (payload.cr3ea_qualitytourid || rowRecord.cr3ea_qualitytourid || "").replace(/[{}]/g, "");
        if (cleanTourId && method === 'POST') {
            payload["cr3ea_qualitytourid@odata.bind"] = `/${RAJPURA_TABLES.QUALITY_TOUR}(${cleanTourId})`;
        }
        delete payload.cr3ea_qualitytourid;

        if (!navigator.onLine) {
            // OFFLINE MODE
            console.warn("Offline: saving checklist row to local DB", payload);
            const id = alcIdVal;
            const tourId = cleanTourId;
            if (id) {
                await putToStore('alc_checkpoints', { id, tourId: tourId, data: payload, syncStatus: 'pending' });
                await enqueueSync({
                    id: `${Date.now()}_chk_${id}`,
                    type: 'checkpoint',
                    method: method,
                    payload: payload,
                    createdAt: Date.now()
                });
            }
            return payload;
        }

        const tokenResult = await getAccessToken(undefined, undefined, true);
        const accessToken = tokenResult?.token;
        if (!accessToken) throw new Error("Access token is missing");

        const apiVersion = "9.2";
        const tableCandidates = [
            RAJPURA_TABLES.ALCS
        ];

        let response: Response | null = null;
        let lastErrText = "";

        for (const tableName of tableCandidates) {
            const currentPayload = { ...payload };
            let url = `${ENVIRONMENT_URL}/api/data/v${apiVersion}/${tableName}`;
            if (method === "PATCH" && alcIdVal) {
                url += `(${alcIdVal})`;
            }

            try {
                const res = await fetch(url, {
                    method: method,
                    headers: {
                        "Accept": "application/json",
                        "Content-Type": "application/json; charset=utf-8",
                        "OData-MaxVersion": "4.0",
                        "OData-Version": "4.0",
                        "Prefer": "return=representation",
                        "Authorization": `Bearer ${accessToken}`
                    },
                    body: JSON.stringify(currentPayload)
                });
                if (res.ok) {
                    response = res;
                    break;
                } else {
                    lastErrText = await res.text();
                }
            } catch (e) {
                lastErrText = String(e);
            }
        }

        if (!response || !response.ok) {
            throw new Error(`Dataverse checklist row save failed: ${lastErrText}`);
        }

        const resultData = method === "PATCH" ? payload : await response.json();
        const savedId = resultData.cr3ea_prod_rajpura_alcsid || resultData.cr3ea_rajpura_alcsid || alcIdVal;

        if (savedId) {
            await putToStore('alc_checkpoints', { id: savedId, tourId: cleanTourId, data: resultData, syncStatus: 'synced' });
        }

        return resultData;
    },

    getCheckpoints: async function (tourId: string) {
        const tokenResult = await getAccessToken(undefined, undefined, true);
        const accessToken = tokenResult?.token;
        if (!accessToken) throw new Error("Access token is missing");

        const apiVersion = "9.2";
        const cleanTourId = tourId ? String(tourId).replace(/[{}]/g, "").trim().toLowerCase() : "";

        const tableCandidates = [
            RAJPURA_TABLES.ALCS
        ];

        const filterCandidates = [
            `?$filter=_cr3ea_qualitytourid_value eq '${cleanTourId}'`,
            `?$filter=cr3ea_qualitytourid eq '${cleanTourId}'`
        ];

        for (const tableName of tableCandidates) {
            for (const filter of filterCandidates) {
                try {
                    const url = `${ENVIRONMENT_URL}/api/data/v${apiVersion}/${tableName}${filter}`;
                    const response = await fetch(url, {
                        method: "GET",
                        headers: {
                            "Accept": "application/json",
                            "OData-MaxVersion": "4.0",
                            "OData-Version": "4.0",
                            "Authorization": `Bearer ${accessToken}`
                        }
                    });
                    if (response.ok) {
                        const data = await response.json();
                        if (data.value && Array.isArray(data.value) && data.value.length > 0) {
                            return data.value;
                        }
                    }
                } catch (_) {}
            }
        }

        return [];
    },

    uploadCorrectiveActionFile: async function (
        fileObject: File,
        tourId: string,
        areaName: string,
        checkpointId: string,
        actionRemarks: string,
        accessToken?: string,
        msalInstance?: any,
        msalAccount?: any
    ) {
        const libraryName = "ALC_CorrectiveActions_Docs";
        const arrayBuffer = await fileObject.arrayBuffer();
        const { SP_SITE_URL, SP_MSAL_SCOPE } = await import('../../../../constants/sharepoint');

        let spToken = "";

        // 1. Try acquiring SharePoint-scoped token via MSAL if instance & account are available
        if (msalInstance && msalAccount) {
            try {
                const tokenRes = await msalInstance.acquireTokenSilent({
                    scopes: [SP_MSAL_SCOPE],
                    account: msalAccount
                });
                if (tokenRes?.accessToken) {
                    spToken = tokenRes.accessToken;
                }
            } catch (e) {
                console.warn("Could not acquire silent MSAL token for SharePoint:", e);
            }
        }

        // 2. Fallback to passed accessToken
        if (!spToken && accessToken) {
            spToken = accessToken;
        }

        if (!spToken) {
            throw new Error("Access token is missing for SharePoint upload.");
        }

        const safeFileName = encodeURIComponent(fileObject.name.replace(/'/g, "''"));
        const uploadUrl = `${SP_SITE_URL}/_api/web/lists/getByTitle('${libraryName}')/RootFolder/Files/add(url='${safeFileName}', overwrite=true)`;

        let uploadResponse = await fetch(uploadUrl, {
            method: "POST",
            headers: {
                "Accept": "application/json;odata=verbose",
                "Authorization": `Bearer ${spToken}`,
                "Content-Type": "application/octet-stream"
            },
            body: arrayBuffer
        });

        // 3. If 401 occurs and MSAL instance is available, force refresh SharePoint token and retry
        if (uploadResponse.status === 401 && msalInstance && msalAccount) {
            try {
                console.warn("SharePoint returned 401. Retrying with fresh MSAL SharePoint token...");
                const tokenRes = await msalInstance.acquireTokenSilent({
                    scopes: [SP_MSAL_SCOPE],
                    account: msalAccount,
                    forceRefresh: true
                });
                if (tokenRes?.accessToken) {
                    spToken = tokenRes.accessToken;
                    uploadResponse = await fetch(uploadUrl, {
                        method: "POST",
                        headers: {
                            "Accept": "application/json;odata=verbose",
                            "Authorization": `Bearer ${spToken}`,
                            "Content-Type": "application/octet-stream"
                        },
                        body: arrayBuffer
                    });
                }
            } catch (retryErr) {
                console.error("Token refresh retry failed:", retryErr);
            }
        }

        if (!uploadResponse.ok) {
            const errorText = await uploadResponse.text();
            throw new Error(`Failed to upload corrective action file: ${uploadResponse.status} - ${errorText}`);
        }

        const uploadedFileData = await uploadResponse.json();
        const listItemUrl = uploadedFileData.d.ListItemAllFields.__deferred.uri;

        // 4. Fetch the ListItem ID to update its metadata
        const listItemResponse = await fetch(listItemUrl, {
            method: "GET",
            headers: {
                "Accept": "application/json;odata=verbose",
                "Authorization": `Bearer ${spToken}`
            }
        });

        if (!listItemResponse.ok) {
            const errorText = await listItemResponse.text();
            throw new Error(`Failed to fetch uploaded file item: ${listItemResponse.status} - ${errorText}`);
        }

        const listItemData = await listItemResponse.json();
        const updateUrl = listItemData.d.__metadata.uri;
        const itemType = listItemData.d.__metadata.type;

        // 5. Update File Metadata
        const metadataPayload = {
            "__metadata": { "type": itemType },
            "QualityTourId": tourId,
            "AreaName": areaName,
            "CheckpointID": checkpointId,
            "ActionTaken": actionRemarks
        };

        const updateResponse = await fetch(updateUrl, {
            method: "POST", // SP 2013 REST requires POST with X-HTTP-Method = MERGE
            headers: {
                "Accept": "application/json;odata=verbose",
                "Content-Type": "application/json;odata=verbose",
                "Authorization": `Bearer ${spToken}`,
                "X-HTTP-Method": "MERGE",
                "IF-MATCH": "*"
            },
            body: JSON.stringify(metadataPayload)
        });

        if (!updateResponse.ok) {
            const errorText = await updateResponse.text();
            throw new Error(`Failed to update corrective action file metadata: ${updateResponse.status} - ${errorText}`);
        }

        return uploadedFileData.d.ServerRelativeUrl;
    },

    getSessionById: async function (tourId: string) {
        const tokenResult = await getAccessToken(undefined, undefined, true);
        const accessToken = tokenResult?.token;
        if (!accessToken) throw new Error("Access token is missing");

        const tableName = RAJPURA_TABLES.QUALITY_TOUR;
        const apiVersion = "9.2";
        const url = `${ENVIRONMENT_URL}/api/data/v${apiVersion}/${tableName}(${tourId})`;

        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "OData-MaxVersion": "4.0",
                "OData-Version": "4.0",
                "Authorization": `Bearer ${accessToken}`
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to fetch tour session: ${response.status} - ${errorText}`);
        }

        return await response.json();
    },

    getActiveSessionsByLine: async function (lineNo: string) {
        const tokenResult = await getAccessToken(undefined, undefined, true);
        const accessToken = tokenResult?.token;
        if (!accessToken) throw new Error("Access token is missing");

        const tableName = RAJPURA_TABLES.QUALITY_TOUR;
        const apiVersion = "9.2";
        // Fetch last 15 tours for this line to ensure we get at least 5 for repeatability analysis
        const filter = `?$filter=cr3ea_lineno eq '${lineNo}'&$orderby=cr3ea_tourstartdate desc&$top=15`;
        const url = `${ENVIRONMENT_URL}/api/data/v${apiVersion}/${tableName}${filter}`;

        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "OData-MaxVersion": "4.0",
                "OData-Version": "4.0",
                "Authorization": `Bearer ${accessToken}`
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to fetch recent line sessions: ${response.status} - ${errorText}`);
        }

        const data = await response.json();
        return data.value;
    }
};
